"use client";

import React from "react";
import Image from "next/image";
import { Tag, Info } from "lucide-react";
import { useFormContext, useWatch } from "react-hook-form";
import { useAtomValue, useAtom } from "jotai";

import { Paragraph } from "@/components/ui/typography";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

import CourseActionButton from "./course-action-button";

import { applyCoupon } from "@/fetchers/store";
import { isAuthenticatedAtom } from "@/store/auth";
import { courseCouponAtom, coursePackagesAtom } from "@/store/course";

import { cn, calculatePercentage, formatCurrency } from "@/lib/utils";

function CourseDetail({ course, discountText, className }) {
  const form = useFormContext();

  const packageId = useWatch({ name: "packageId" });
  const isAuthenticated = useAtomValue(isAuthenticatedAtom);
  const [courseCoupon, setCourseCoupon] = useAtom(courseCouponAtom);
  const coursePackages = useAtomValue(coursePackagesAtom);

  const currentPackage = React.useMemo(() => {
    if (!packageId || coursePackages?.length === 0) return;

    return coursePackages.find((pkg) => pkg.packageId === packageId);
  }, [coursePackages, packageId]);

  React.useEffect(() => {
    if (isAuthenticated && currentPackage) {
      const couponCode = currentPackage.offers?.[0]?.couponCode || null;
      if (!couponCode) return;

      const parentCourseId = form.getValues("parentCourseId");

      applyCoupon({
        parentCourseId,
        courseId: currentPackage.courseId,
        languageId: currentPackage.languageId,
        packageId: currentPackage.packageId,
        couponCode,
      }).then((coupon) => {
        if (coupon) {
          form.setValue("couponCode", coupon.couponCode);
          setCourseCoupon(coupon);
        } else {
          form.setValue("couponCode", null);
          setCourseCoupon(null);
        }
      });
    }
  }, [form, isAuthenticated, currentPackage, setCourseCoupon]);

  const sellingPrice =
    courseCoupon?.sellingPrice || currentPackage?.packageSellingPrice;

  const percentageOff = calculatePercentage(
    sellingPrice,
    currentPackage?.packageBasePrice,
    true
  );

  const couponPercentageOff = calculatePercentage(
    courseCoupon?.sellingPrice,
    courseCoupon?.basePrice,
    true
  );

  const handleOnHelpClick = () => {
    document.getElementById("course-support").scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
  };

  return (
    <div
      className={cn(
        "flex flex-col md:gap-y-6",
        "w-full fixed md:sticky top-auto md:top-18 md:-mt-68.5 z-50 md:z-20",
        "bottom-0 md:bottom-auto left-0 md:left-auto pt-3",
        className
      )}
    >
      <div
        className={cn(
          "md:shadow md:p-px rounded-xl md:rounded-lg",
          "bg-linear-to-b from-transparent md:from-primary to-black/70 md:to-primary-foreground"
        )}
      >
        <div className="flex flex-col bg-primary-foreground h-full md:p-3 rounded-lg">
          <div className="hidden md:block relative bg-grayscale-line aspect-video-course-card-thumbnail rounded-lg mb-3 overflow-hidden">
            <Image src={course.thumbnail} alt={course.courseTitle} fill />
          </div>

          <Paragraph
            variant="b3"
            weight="sb"
            className="hidden md:block group-aria-[disabled=no]/course-form:mb-5"
          >
            {course.courseTitle}
          </Paragraph>

          {sellingPrice > 0 && (
            <div
              className={cn(
                "flex items-center-safe justify-between md:items-start",
                "md:justify-start md:flex-col gap-y-1 md:mb-3 py-2 px-4 md:p-0",
                "group-aria-[disabled=yes]/course-form:hidden"
              )}
            >
              <div className="flex flex-col md:flex-row gap-x-2 gap-y-1 md:items-center">
                <Paragraph
                  variant="b3-tight"
                  weight="bold"
                  className="text-grayscale-header"
                  as="span"
                >
                  {formatCurrency(sellingPrice)}
                </Paragraph>

                {percentageOff > 0 && (
                  <div className="flex items-center gap-x-2">
                    <Paragraph
                      variant="b5-tight"
                      className="text-grayscale-placeholder line-through"
                      as="span"
                    >
                      {formatCurrency(currentPackage?.packageBasePrice)}
                    </Paragraph>
                    <Paragraph
                      variant="b5-tight"
                      weight="sb"
                      className="text-success"
                      as="span"
                    >
                      {percentageOff}% off
                    </Paragraph>
                  </div>
                )}
              </div>

              {courseCoupon && couponPercentageOff > 0 && (
                <div className="flex items-center gap-x-2 px-2 md:px-0 rounded-xl bg-success-bg md:bg-transparent">
                  <Tag className="fill-success text-white size-3" />
                  <Paragraph variant="b5" weight="sb" className="text-success">
                    Coupon Applied - Extra {couponPercentageOff}% Off
                  </Paragraph>
                </div>
              )}
            </div>
          )}

          {discountText && (
            <Badge className="hidden md:inline-flex bg-primary-bg text-primary rounded-xl whitespace-normal group-aria-[disabled=yes]/course-form:hidden">
              Limited-time pricing. {discountText}
            </Badge>
          )}

          <div className="flex gap-12 md:mt-6 border-t md:border-t-0 border-grayscale-line py-2 px-4 md:p-0">
            <CourseActionButton course={course} />
          </div>
        </div>
      </div>

      <Button
        type="button"
        variant="secondary"
        size="small"
        onClick={handleOnHelpClick}
        className="bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-md"
      >
        <Info className="mr-1" />
        <Paragraph variant="b4-tight" weight="normal" className="text-inherit">
          Looking for help? Tap here
        </Paragraph>
      </Button>
    </div>
  );
}

export default CourseDetail;
